// eslint-disable-next-line no-unused-vars
import React from 'react';
import { func, bool, string, number } from 'prop-types';
import { Button, Loading, TextArea } from '@carbon/react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import uniqueId from 'lodash/uniqueId';
import { Star } from 'lucide-react';

const StyledFeedbackContainer = styled.div`
  width: 100%;
  background-color: ${ props => props.theme.products.colors.addToFavorite };
  padding: 30px;
  margin-bottom: 20px;
  text-align: center;

  .title {
    font-size: 1.56rem;
    font-weight: 600;
    color: ${ props => props.theme.colors.darkGray };
  }

  .rating {
    margin: 10px auto;
    cursor: pointer;
    display: flex;
    justify-content: center;

    li {
      padding: 0 3px;
      text-align: center;
    }
    .star {
      margin: 0 auto;
      color: ${ props => props.theme.colors.anotherGray };

      &:hover,
      &.hover,
      &.selected {
        color: ${ props => props.theme.colors.lightOrange };
      }
    }
  }

  .message {
    margin: 5px 0;
    font-size: 0.875rem;
    line-height: 1rem;
    color: ${ props => props.theme.colors.darkGray };
  }

  .feedback {
    max-width: 70%;
    margin: 15px auto;
    border: 0;
    outline: 0;
  }

  .submit {
    display: flex;
    justify-content: center;
    width: 100%;
    height: 40px;
    max-width: 200px;
    min-width: 180px;
    padding: 0;
    margin: 0 auto;
    font-size: 1rem;
    &:not(:disabled) {
      color: ${ props => props.theme.colors.white };
      background-color: ${ props => props.theme.colors.default };
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    .feedback, .submit {
      max-width: unset;
    }
  }
`;

const FeedbackForm = (
  {
    orderId,
    error,
    success,
    loading,
    rating,
    tempRating,
    onMouseOver,
    onMouseOut,
    handleOnSetRating,
    handleOnChange,
    onSubmit
  }
) => {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <StyledFeedbackContainer theme={ theme }>
      <p className="title">{ t('order.feedback.title') }</p>
      <ul className="rating">
        { Array.from({ length: 5 }, (v, idx) => (
          <li
            key={ uniqueId('star_') }
            onMouseEnter={ () => onMouseOver(idx + 1) }
            onMouseLeave={ onMouseOut }
          >
            <Star
              className={ `star ${(tempRating || rating) >= idx + 1 ? 'selected' : ''}` }
              onClick={ () => handleOnSetRating(idx + 1) }
            />
          </li>
        )) }
      </ul>
      <p className="message">{ t(`order.feedback.ratings.${rating}`) }</p>
      <TextArea
        className="feedback"
        labelText=""
        id="feedback"
        name="feedback"
        light
        placeholder={ t('order.feedback.opinion') }
        onChange={ handleOnChange }
      />
      { error && <p className="error">{ error }</p> }
      <Button
        disabled={ loading }
        className="submit"
        onClick={ onSubmit }
        data-test="onSubmitFeedback"
        data-testid={ orderId }
        kind="danger"
      >
        { loading && <Loading active={ loading } withOverlay={ false } small /> }
        { t('order.feedback.send') }
      </Button>
      { success && <p className="message success">{ t('order.feedback.success') }</p> }
    </StyledFeedbackContainer>
  );
};

FeedbackForm.propTypes = {
  rating: number,
  orderId: string,
  tempRating: number,
  loading: bool,
  success: bool,
  error: string,
  onMouseOver: func,
  onMouseOut: func,
  handleOnSetRating: func,
  handleOnChange: func,
  onSubmit: func
};
FeedbackForm.displayName = 'FeedbackFormComponent';
export default FeedbackForm;
